import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type {
  ApiTokenCreatedResponse,
  ApiTokenDetailsResponse,
  ApiTokenResponse,
  ApiTokenUsageStatsResponse,
  CreateApiTokenRequest,
  GetApiTokenUsageQuery,
  GetApiTokensRequest,
  GetApiTokensResponse,
  UpdateApiTokenRequest,
} from '@mai/platform-api/src/routes/api-tokens.schema';

import { type QueryOptions, apiClient } from './axios';

// API Token CRUD operations
export const fetchApiTokens = async (
  params?: GetApiTokensRequest,
): Promise<GetApiTokensResponse> => {
  const response = await apiClient.get('/api-tokens', { params });
  return response.data;
};

export const fetchApiTokenById = async (
  tokenId: string,
): Promise<ApiTokenDetailsResponse> => {
  const response = await apiClient.get(`/api-tokens/${tokenId}`);
  return response.data;
};

export const createApiToken = async (
  data: CreateApiTokenRequest,
): Promise<ApiTokenCreatedResponse> => {
  const response = await apiClient.post('/api-tokens', data);
  return response.data;
};

export const updateApiToken = async ({
  tokenId,
  data,
}: {
  tokenId: string;
  data: UpdateApiTokenRequest;
}): Promise<ApiTokenResponse> => {
  const response = await apiClient.patch(`/api-tokens/${tokenId}`, data);
  return response.data;
};

export const deleteApiToken = async (tokenId: string): Promise<void> => {
  await apiClient.delete(`/api-tokens/${tokenId}`);
};

export const fetchApiTokenUsage = async (
  tokenId: string,
  params?: GetApiTokenUsageQuery,
): Promise<ApiTokenUsageStatsResponse> => {
  const response = await apiClient.get(`/api-tokens/${tokenId}/usage`, {
    params,
  });
  return response.data;
};

// Query Hooks
export const useApiTokens = (
  teamId: string | null,
  filters?: Omit<GetApiTokensRequest, 'teamId'>,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['api-tokens', teamId, filters],
    queryFn: () => fetchApiTokens({ teamId: teamId!, ...filters }),
    enabled: !!teamId && options?.enabled !== false,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

export const useApiToken = (tokenId: string | null, options?: QueryOptions) => {
  return useQuery({
    queryKey: ['api-tokens', 'by-id', tokenId],
    queryFn: () => fetchApiTokenById(tokenId!),
    enabled: !!tokenId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useApiTokenUsage = (
  tokenId: string | null,
  filters?: GetApiTokenUsageQuery,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['api-tokens', 'usage', tokenId, filters],
    queryFn: () => fetchApiTokenUsage(tokenId!, filters),
    enabled: !!tokenId && options?.enabled !== false,
    retry: options?.retry,
  });
};

// Mutation Hooks
export const useCreateApiToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createApiToken,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
  });
};

export const useUpdateApiToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateApiToken,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
  });
};

export const useDeleteApiToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteApiToken,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
  });
};
